import { useEffect, useRef, useState } from "react";
import { breakingTicker, navLinks, trendingSearch } from "@/mocks/guatian";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (k: string) => {
    setKeyword(k);
    setSearchOpen(false);
    document.getElementById("updates")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* 实时滚动条 */}
      <div className="bg-accent-500 text-background-50 overflow-hidden">
        <div className="mx-auto flex max-w-[1280px] items-center gap-4 px-4 md:px-6">
          <span className="relative z-10 shrink-0 whitespace-nowrap py-2 text-xs font-bold tracking-wider">
            <i className="ri-flashlight-fill mr-1 text-secondary-300" aria-hidden="true"></i>实时快讯
          </span>
          <div className="relative flex h-8 flex-1 items-center overflow-hidden">
            <div className="gt-marquee flex whitespace-nowrap">
              {[...breakingTicker, ...breakingTicker].map((t, i) => (
                <span key={i} className="mx-6 inline-flex items-center text-sm">
                  <i className="ri-fire-fill mr-2 text-secondary-300" aria-hidden="true"></i>
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 吸顶导航 */}
      <header
        className={`sticky top-0 z-40 transition-all ${
          scrolled
            ? "border-b border-background-200 bg-background-50/90 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.02)]"
            : "bg-background-50"
        }`}
      >
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-4 px-4 py-3 md:px-6">
          <a href="#top" className="group flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-700 text-background-50">
              <i className="ri-thunderstorms-fill text-lg" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className="text-2xl font-normal text-foreground-950"
                style={{ fontFamily: "var(--font-label)" }}
              >
                大雷<span className="text-accent-600">网站</span>
              </span>
              <span className="mt-1 text-[11px] tracking-[0.18em] text-foreground-500">
                吃瓜追更 · 反转不止
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                className="rounded-md px-3 py-2 text-sm text-foreground-700 transition hover:bg-background-100 hover:text-foreground-950 whitespace-nowrap"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative hidden md:block" ref={searchRef}>
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") pick(keyword || trendingSearch[0]);
                }}
                placeholder="搜热点 / 事件"
                className="w-48 rounded-md border border-background-200 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-800 outline-none transition focus:w-60 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                aria-label="搜索热点"
              />
              {searchOpen && (
                <div className="absolute right-0 top-11 z-50 w-64 rounded-lg border border-background-200 bg-background-50 p-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.18)]">
                  <p className="px-2 py-1.5 text-xs font-medium text-foreground-400">大家都在搜</p>
                  {trendingSearch.map((t) => (
                    <button
                      key={t}
                      onClick={() => pick(t)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground-700 transition hover:bg-background-100"
                    >
                      <i className="ri-search-line text-foreground-300" aria-hidden="true"></i>
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#subscribe"
              className="hidden whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-bold text-background-50 transition hover:bg-primary-700 sm:inline-flex"
            >
              订阅追更
              <i className="ri-bell-line" aria-hidden="true"></i>
            </a>

            <button
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-xl text-foreground-700 transition hover:bg-background-100 lg:hidden"
              aria-label="打开菜单"
            >
              <i className={open ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 lg:hidden">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm text-foreground-800 transition hover:bg-background-100"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#subscribe"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-bold text-background-50"
              >
                订阅追更 <i className="ri-bell-line" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}